import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Plus, Minus } from "lucide-react";
import axios from "axios";

const API_URL = "https://accessoriesgirly.onrender.com/api/products";

const containerVariant = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const imageVariant = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6 } },
};

const textVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function ProductSectionNav() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("low-high");
  const [view, setView] = useState("grid");
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const res = await axios.get(API_URL);
        setProducts(res.data);
      } catch (err) {
        console.error("Error fetching products:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Load cart from localStorage
  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
    setCart(savedCart);
  }, []);

  const updateCart = (updatedCart) => {
    setCart(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    window.dispatchEvent(new Event("storage"));
  };

  const addToCart = (product) => {
    const existing = cart.find((item) => item.id === product._id);
    if (existing) {
      updateCart(
        cart.map((item) =>
          item.id === product._id ? { ...item, quantity: item.quantity + 1 } : item
        )
      );
    } else {
      updateCart([...cart, { ...product, id: product._id, quantity: 1 }]);
    }
  };

  const decreaseQty = (product) => {
    const existing = cart.find((item) => item.id === product._id);
    if (!existing) return;
    if (existing.quantity === 1) {
      updateCart(cart.filter((item) => item.id !== product._id));
    } else {
      updateCart(
        cart.map((item) =>
          item.id === product._id ? { ...item, quantity: item.quantity - 1 } : item
        )
      );
    }
  };

  const getQuantity = (productId) =>
    cart.find((item) => item.id === productId)?.quantity || 0;

  const handleBuyNow = (product) => {
    const existing = cart.find((item) => item.id === product._id);
    let updatedCart;
    if (existing) {
      updatedCart = cart.map((item) =>
        item.id === product._id ? { ...item, quantity: item.quantity + 1 } : item
      );
    } else {
      updatedCart = [...cart, { ...product, id: product._id, quantity: 1 }];
    }
    updateCart(updatedCart);
    navigate("/cart");
  };

  const filteredProducts = products
    .filter((product) => product.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) =>
      sort === "low-high" ? a.price - b.price : b.price - a.price
    );

  const calculateDiscount = (price, offerPrice) => {
    if (!offerPrice) return 0;
    return Math.round(((price - offerPrice) / price) * 100);
  };

  if (loading) {
    return (
      <div className="py-12 text-center text-gray-600">
        Loading products...
      </div>
    );
  }

  return (
    <section className="py-12 bg-pink-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2
          className="text-3xl font-bold text-center text-pink-600 mb-6"
          variants={textVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          Our Products
        </motion.h2>

        {/* Controls */}
        <motion.div
          className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4"
          variants={containerVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <input
            type="text"
            placeholder="Search products..."
            className="px-4 py-2 rounded-lg border border-pink-300 focus:outline-none focus:ring-2 focus:ring-pink-500 w-full sm:w-1/3"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            className="px-4 py-2 rounded-lg border border-pink-300 focus:outline-none focus:ring-2 focus:ring-pink-500"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="low-high">Price: Low to High</option>
            <option value="high-low">Price: High to Low</option>
          </select>

          <div className="flex gap-2">
            <button
              onClick={() => setView("grid")}
              className={`px-4 py-2 rounded-lg ${view === "grid" ? "bg-pink-600 text-white" : "bg-white border border-pink-300"}`}
            >
              Grid View
            </button>
            <button
              onClick={() => setView("list")}
              className={`px-4 py-2 rounded-lg ${view === "list" ? "bg-pink-600 text-white" : "bg-white border border-pink-300"}`}
            >
              List View
            </button>
          </div>
        </motion.div>

        {/* Products */}
        {view === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {filteredProducts.map((product) => {
              const qty = getQuantity(product._id);
              const discount = calculateDiscount(product.price, product.offerPrice);
              return (
                <motion.div
                  key={product._id}
                  className="group relative bg-white border border-pink-200 shadow-md rounded-2xl overflow-hidden transform transition-all duration-300 hover:scale-105 hover:-rotate-1 hover:shadow-2xl hover:border-pink-400"
                  variants={containerVariant}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                >
                  {/* Discount Badge */}
                  {discount > 0 && (
                    <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-lg z-10">
                      {discount}% OFF
                    </div>
                  )}

                  <motion.div
                    className="overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/productInfo/${product._id}`)}
                    variants={imageVariant}
                  >
                    <img
                      src={product.images?.[0] || "https://via.placeholder.com/300x200"}
                      alt={product.name}
                      className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                  </motion.div>

                  <motion.div className="p-4 text-center" variants={textVariant}>
                    <h3 className="text-lg font-semibold text-gray-800 group-hover:text-pink-600 transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-pink-600 font-bold mt-2">
                      {product.offerPrice ? (
                        <>
                          <span>₹{product.offerPrice.toFixed(2)}</span>{" "}
                          <span className="line-through text-gray-400">₹{product.price.toFixed(2)}</span>
                        </>
                      ) : (
                        <>₹{product.price.toFixed(2)}</>
                      )}
                    </p>

                    <div className="mt-4 flex justify-center gap-2">
                      {qty === 0 ? (
                        <button
                          className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                          onClick={() => addToCart(product)}
                        >
                          Add to Cart
                        </button>
                      ) : (
                        <div className="flex items-center gap-3">
                          <button
                            className="p-2 rounded-full bg-gray-200 hover:bg-gray-300"
                            onClick={() => decreaseQty(product)}
                          >
                            <Minus className="w-4 h-4 text-gray-700" />
                          </button>
                          <span className="px-4 py-1 bg-pink-100 text-pink-700 font-bold rounded-lg">
                            {qty}
                          </span>
                          <button
                            className="p-2 rounded-full bg-gray-200 hover:bg-gray-300"
                            onClick={() => addToCart(product)}
                          >
                            <Plus className="w-4 h-4 text-gray-700" />
                          </button>
                        </div>
                      )}
                      <button
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        onClick={() => handleBuyNow(product)}
                      >
                        Buy Now
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {filteredProducts.map((product) => {
              const qty = getQuantity(product._id);
              const discount = calculateDiscount(product.price, product.offerPrice);
              return (
                <motion.div
                  key={product._id}
                  className="flex flex-col sm:flex-row relative bg-white border border-pink-200 shadow-md rounded-2xl overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                  variants={containerVariant}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                >
                  {/* Discount Badge */}
                  {discount > 0 && (
                    <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-lg z-10">
                      {discount}% OFF
                    </div>
                  )}

                  <motion.img
                    src={product.images?.[0] || "https://via.placeholder.com/300x200"}
                    alt={product.name}
                    className="w-full sm:w-48 h-48 object-cover"
                    variants={imageVariant}
                  />
                  <motion.div className="p-4 flex flex-col justify-between" variants={textVariant}>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800">{product.name}</h3>
                      <p className="text-pink-600 font-bold mt-2">
                        {product.offerPrice ? (
                          <>
                            <span>₹{product.offerPrice.toFixed(2)}</span>{" "}
                            <span className="line-through text-gray-400">₹{product.price.toFixed(2)}</span>
                          </>
                        ) : (
                          <>₹{product.price.toFixed(2)}</>
                        )}
                      </p>
                    </div>
                    <div className="mt-4 flex gap-2">
                      {qty === 0 ? (
                        <button
                          className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                          onClick={() => addToCart(product)}
                        >
                          Add to Cart
                        </button>
                      ) : (
                        <div className="flex items-center gap-3">
                          <button
                            className="p-2 rounded-full bg-gray-200 hover:bg-gray-300"
                            onClick={() => decreaseQty(product)}
                          >
                            <Minus className="w-4 h-4 text-gray-700" />
                          </button>
                          <span className="px-4 py-1 bg-pink-100 text-pink-700 font-bold rounded-lg">
                            {qty}
                          </span>
                          <button
                            className="p-2 rounded-full bg-gray-200 hover:bg-gray-300"
                            onClick={() => addToCart(product)}
                          >
                            <Plus className="w-4 h-4 text-gray-700" />
                          </button>
                        </div>
                      )}
                      <button
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        onClick={() => handleBuyNow(product)}
                      >
                        Buy Now
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
